import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush } from 'recharts';
import { HiMinus, HiPlus, HiRefresh } from 'react-icons/hi';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const ChartInclination = ({ DataSource }) => {
    const [brushData, setBrushData] = useState(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [zoomRange, setZoomRange] = useState({ start: 0, end: 100 });

    // Memoized data processing for better performance
    const chartData = useMemo(() => {
        // Filter data untuk sensor Inclino (INC) secara dinamis
        const inclinoData = DataSource.filter(item => item?.SensorCode?.startsWith("INC"));

        // console.log(DataSource);

        // Proses data untuk Inclino dan sort by timestamp
        const processedData = inclinoData.map(inclinoItem => {
            return {
                refDate: dayjs.utc(inclinoItem.RefDate).format('YYYY-MM-DD'),
                refTime: dayjs.utc(inclinoItem.RefDate).format('HH:mm:ss'),
                refDateTime: dayjs.utc(inclinoItem.RefDate).format('YYYY-MM-DD HH:mm:ss'),
                refDateTimeFull: inclinoItem.RefDate,
                timestamp: inclinoItem.RefDate,
                Inclino1: inclinoItem.Value1,
                Inclino2: inclinoItem.Value2
            };
        });

        // Sort by timestamp ascending
        return processedData.sort((a, b) => a.refDateTime - b.refDateTime);
    }, [DataSource]);

    // Calculate display data based on zoom
    const displayData = useMemo(() => {
        if (brushData) {
            return chartData.slice(brushData.startIndex, brushData.endIndex + 1);
        }

        if (zoomLevel > 1) {
            const totalPoints = chartData.length;
            const visiblePoints = Math.floor(totalPoints / zoomLevel);
            const startIndex = Math.floor((zoomRange.start / 100) * (totalPoints - visiblePoints));
            const endIndex = startIndex + visiblePoints;
            return chartData.slice(startIndex, endIndex);
        }

        return chartData;
    }, [chartData, brushData, zoomLevel, zoomRange]);

    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 0.1, 5));
        setBrushData(null); // Clear brush selection when using button zoom
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 0.1, 1));
        setBrushData(null); // Clear brush selection when using button zoom
    };

    const handleResetZoom = () => {
        setZoomLevel(1);
        setZoomRange({ start: 0, end: 100 });
        setBrushData(null);
    };

    const handleZoomRangeChange = (start, end) => {
        setZoomRange({ start, end });
    };

    return (
        <div className="p-4 flex flex-col gap-4 w-full">
            <div className="flex gap-2 justify-end">
                <button
                    onClick={handleZoomOut}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Zoom Out"
                    disabled={zoomLevel <= 1}
                >
                    <HiMinus className="w-4 h-4" />
                </button>

                <span className="px-3 py-1 bg-gray-700 text-white rounded text-sm font-medium">
                    {Math.round(zoomLevel * 100)}%
                </span>

                <button
                    onClick={handleZoomIn}
                    className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
                    title="Zoom In"
                    disabled={zoomLevel >= 5}
                >
                    <HiPlus className="w-4 h-4" />
                </button>

                <button
                    onClick={handleResetZoom}
                    className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                    title="Reset Zoom"
                >
                    <HiRefresh className="w-4 h-4" />
                </button>
            </div>

            <div className="flex justify-evenly lg:flex-row items-center gap-12 flex-col relative">
                <ResponsiveContainer width="100%" height={window.innerWidth > 768 ? 400 : 250}>
                    <LineChart data={displayData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                            dataKey="refTime"
                            stroke="#374151"
                            tick={{ fill: "#374151", fontSize: 12 }}
                            tickLine={{ stroke: "#374151" }}
                            axisLine={{ stroke: "#374151", strokeWidth: 2 }}
                            label={{ osition: "bottom", offset: 0, style: { fill: "#374151", fontSize: 14, fontWeight: "bold" } }}
                            angle={-45}
                            textAnchor="end"
                            height={80}
                        />
                        <YAxis
                            stroke="#374151"
                            tick={{ fill: "#374151", fontSize: 12 }}
                            tickLine={{ stroke: "#374151" }}
                            axisLine={{ stroke: "#374151", strokeWidth: 2 }}
                            domain={['auto', 'auto']}
                            label={{ angle: -90, position: "left", offset: 0, style: { fill: "#374151", fontSize: 14, fontWeight: "bold" } }}
                            width={80}
                            tickFormatter={(value) => `${value}°`}
                        />
                        <Tooltip
                            labelFormatter={(value) => {
                                const dataIndex = displayData.findIndex(item => item.refDateTime === value);
                                return dataIndex !== -1 ? displayData[dataIndex].refDateTime : value;
                            }}
                            formatter={(value, name) => [`${value}°`, name]}
                            labelStyle={{ color: "#FFFFFF" }}
                            itemStyle={{ color: "#FFFFFF" }}
                            contentStyle={{
                                backgroundColor: "#1e2939",
                                borderColor: "#4B5563",
                                borderRadius: "8px",
                                border: "1px solid #4B5563"
                            }}
                        />
                        <Legend wrapperStyle={{ color: "#374151" }} iconType="line" />
                        <Line
                            type="monotone"
                            dataKey="Inclino1"
                            stroke="#1CA449"
                            strokeWidth={zoomLevel > 2 ? 3 : 2}
                            dot={{
                                fill: "#1CA449",
                                strokeWidth: 2,
                                r: zoomLevel > 2 ? 4 : 3,
                                display: zoomLevel > 3 ? 'block' : 'none'
                            }}
                            activeDot={{
                                r: zoomLevel > 2 ? 6 : 5,
                                stroke: "#374151",
                                strokeWidth: 1.5
                            }}
                            connectNulls={false}
                            animationDuration={300}
                            name="INCLINO 1"
                        />
                        <Line
                            type="monotone"
                            dataKey="Inclino2"
                            stroke="#000000"
                            strokeWidth={zoomLevel > 2 ? 3 : 2}
                            dot={{
                                fill: "#000000",
                                strokeWidth: 2,
                                r: zoomLevel > 2 ? 4 : 3,
                                display: zoomLevel > 3 ? 'block' : 'none'
                            }}
                            activeDot={{
                                r: zoomLevel > 2 ? 6 : 5,
                                stroke: "#374151",
                                strokeWidth: 1.5
                            }}
                            connectNulls={false}
                            animationDuration={300}
                            name="INCLINO 2"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </div>

            {/* Zoom Range Slider (optional) */}
            {zoomLevel > 1 && (
                <div className="flex items-center gap-4 mt-4">
                    <span className="text-white text-sm">Zoom Range:</span>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={zoomRange.start}
                        onChange={(e) => handleZoomRangeChange(parseInt(e.target.value), zoomRange.end)}
                        className="flex-1 h-2 bg-gray-400 rounded-lg appearance-none cursor-pointer"
                    />
                    <span className="text-white text-sm">{zoomRange.start}% - {zoomRange.end}%</span>
                </div>
            )}
        </div>
    );
};

export default ChartInclination;